import { observer } from 'mobx-react-lite';
import {
    insertPicturesInlineTooltip_0,
    emojiTooltip_0,
    formattingOptionsTooltipHide_0,
    formattingOptionsTooltip_0,
    proofingSettings_0,
    turnLightsOn,
    turnLightsOff,
    attachmentsNotSupportedTooltip,
} from './FullComposeEditorAreaBottom.locstring.json';
import AttachToolbarButton from './AttachToolbarButton';
import { createAttachmentsFromFiles } from '../actions/createAttachmentsFromFiles';
import toggleEmojiPicker from '../utils/toggleEmojiPicker';
import toggleRibbon from '../utils/toggleRibbon';
import {
    ComposeUniqueState,
    AttachmentLocalComputerFilePicker,
    IAttachmentLocalComputerFilePicker,
    CreateAttachmentHandlerParams,
    lazyLogAttachOrShareDatapoint,
    FilePickerAction,
    FileSelectionFilter,
    lazyInitializeAttachmentFilePicker,
} from 'owa-attachment-filepicker';
import ProjectionContext from 'owa-popout-v2/lib/context/ProjectionContext';
import { IconButton } from '@fluentui/react/lib/Button';
import { Image } from '@fluentui/react/lib/Image';
import { attachmentMultiMenuEntry } from 'owa-attachment-multi-menu-actions';
import type { AttachmentFileList, CreateAttachmentsProperties } from 'owa-attachment-well-data';
import { isOnGroupCalendar } from 'owa-calendar-event-capabilities';
import { preloadUploadFolder } from 'owa-attachments-upload-folder';
import { onIsDraftChanged, saveEventDraft, setIsDarkMode } from 'owa-calendar-compose-form-actions';
import openFilePickerAndAttach from 'owa-calendar-compose-form-store/lib/actions/openFilePickerAndAttach';
import { getComposeStore, getFullComposeViewState } from 'owa-calendar-compose-store-manager';
import getEventClientItemId from 'owa-calendar-compose-form-store/lib/utils/getEventClientItemId';
import type AttachmentWellHost from 'owa-calendar-meeting-forms-common-store/lib/store/schema/AttachmentWellHost';
import { Ribbon } from 'owa-editor-ribbonplugin';
import isExpressionPaneOpen from 'owa-expressions-store/lib/utils/isExpressionPaneOpen';
import type AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import { getOwaResourceUrl } from 'owa-resource-url';
import { ControlIcons } from 'owa-control-icons';
import { showEditorSettings, isProofingSupported } from 'owa-editor-proofing-common';
import { accCheckerMenuItemText } from 'owa-locstrings/lib/strings/acccheckermenuitemtext.locstring.json';
import onOpenAccessibilityChecker from 'owa-calendar-compose-form-actions/lib/actions/onOpenAccessibilityChecker';

import * as React from 'react';
import { PerformanceDatapoint, DatapointStatus, logUsage } from 'owa-analytics';
import { getIsDarkTheme } from 'owa-fabric-theme';
import { isConnectedAccount } from 'owa-accounts-store';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import loc, { isCurrentCultureRightToLeft } from 'owa-localize';

let styles = require('./FullComposeEditorAreaBottom.scss');
let classNames = require('classnames/bind').bind(styles);

const INLINEIMAGE_SVG = require('./icons/inlineimage.svg');
const EMOJI_SVG = require('./icons/emoji.svg');
const SHOWRIBBON_SVG = require('./icons/showribbon.svg');
const PROOFING_SVG = require('./icons/spelling.svg');

export interface FullComposeEditorAreaBottomProps {
    formId: string | null;
    className?: string;
}

export default observer(function FullComposeEditorAreaBottom(
    props: FullComposeEditorAreaBottomProps
) {
    const { formId, className } = props;
    const targetWindow = React.useContext(ProjectionContext);
    React.useEffect(() => {
        lazyInitializeAttachmentFilePicker
            .import()
            .then(initializeAttachmentFilePicker => initializeAttachmentFilePicker());
        preloadUploadFolder();

        if (getIsDarkTheme()) {
            // If dark mode flight is ON and OWA is in dark mode, we show the toggle. So log it
            logUsage('CalDarkModeToggleShown');
        }
    }, []);

    const toggleDarkMode = React.useCallback(() => {
        setIsDarkMode(!getFullComposeViewState(formId).isDarkMode);
    }, [formId]);

    const attachmentLocalComputerFilePicker = React.useRef<
        React.RefObject<IAttachmentLocalComputerFilePicker>
    >(React.createRef());
    const attachmentLocalComputerToCloudFilePicker = React.useRef<
        React.RefObject<IAttachmentLocalComputerFilePicker>
    >(React.createRef());
    const insertInlineImageFilePicker = React.useRef<
        React.RefObject<IAttachmentLocalComputerFilePicker>
    >(React.createRef());
    const openLocalComputerFilePickerAndAttach = () => {
        saveEventAsDraftIfNewEvent(formId);
        const datapoint = new PerformanceDatapoint('AttachLocalComputerClicked');
        datapoint.addCustomData(['CalendarFullComposeToolBar']);
        if (attachmentLocalComputerFilePicker.current.current) {
            attachmentLocalComputerFilePicker.current.current.click();
            datapoint.end();
        } else {
            datapoint.endWithError(
                DatapointStatus.ClientError,
                new Error('Lazy component not loaded')
            );
        }
    };
    const openLocalComputerFilePickerAndShare = () => {
        saveEventAsDraftIfNewEvent(formId);
        const datapoint = new PerformanceDatapoint('AttachUploadAndSharedClicked');
        datapoint.addCustomData(['CalendarFullComposeToolBar']);
        if (attachmentLocalComputerToCloudFilePicker.current.current) {
            attachmentLocalComputerToCloudFilePicker.current.current.click();
            datapoint.end();
        } else {
            datapoint.endWithError(
                DatapointStatus.ClientError,
                new Error('Lazy component not loaded')
            );
        }
    };
    const addInlineImage = () => {
        saveEventAsDraftIfNewEvent(formId);
        insertInlineImageFilePicker.current.current &&
            insertInlineImageFilePicker.current.current.click();
    };
    let { event, uiState } = getComposeStore(formId);
    if (!event) {
        return null;
    }
    const fullComposeViewState = getFullComposeViewState(formId);
    const attachmentWellHost = uiState as AttachmentWellHost;
    const cloudFilePicker = () => openCloudFilePicker(targetWindow, formId);
    const shouldDisableAttachments = isConnectedAccount(
        event.ParentFolderId.mailboxInfo.userIdentity
    );
    return (
        <div className={classNames(styles.bottom, className)}>
            {fullComposeViewState.isRibbonShown && (
                <div className={styles.ribbon}>
                    <Ribbon
                        editorViewState={fullComposeViewState.bodyViewState}
                        viewState={fullComposeViewState.ribbon}
                        isRtl={isCurrentCultureRightToLeft()}
                        isDarkMode={fullComposeViewState.isDarkMode}
                    />
                </div>
            )}
            <div className={styles.editorToolBar}>
                <AttachToolbarButton
                    openCloudFilePicker={cloudFilePicker}
                    openLocalComputerFilePickerAndAttach={openLocalComputerFilePickerAndAttach}
                    openLocalComputerFilePickerAndShare={openLocalComputerFilePickerAndShare}
                />
                <TooltipHost
                    content={
                        shouldDisableAttachments
                            ? loc(attachmentsNotSupportedTooltip)
                            : loc(insertPicturesInlineTooltip_0)
                    }>
                    <IconButton
                        className={classNames(styles.toolbarButton, {
                            connectedAccount: shouldDisableAttachments,
                        })}
                        onClick={addInlineImage}
                        disabled={shouldDisableAttachments}
                        ariaLabel={
                            shouldDisableAttachments
                                ? loc(attachmentsNotSupportedTooltip)
                                : loc(insertPicturesInlineTooltip_0)
                        }>
                        <Image
                            className={styles.image}
                            shouldFadeIn={false}
                            alt={''}
                            src={getOwaResourceUrl(INLINEIMAGE_SVG)}
                        />
                    </IconButton>
                </TooltipHost>
                <TooltipHost content={loc(emojiTooltip_0)}>
                    <IconButton
                        className={classNames(styles.toolbarButton, {
                            checked: isExpressionPaneOpen(fullComposeViewState.expressionId),
                        })}
                        ariaLabel={loc(emojiTooltip_0)}
                        onClick={toggleEmojiPicker}>
                        <Image
                            className={styles.image}
                            shouldFadeIn={false}
                            alt={''}
                            src={getOwaResourceUrl(EMOJI_SVG)}
                        />
                    </IconButton>
                </TooltipHost>
                <TooltipHost
                    content={
                        fullComposeViewState.isRibbonShown
                            ? loc(formattingOptionsTooltipHide_0)
                            : loc(formattingOptionsTooltip_0)
                    }>
                    <IconButton
                        className={classNames(styles.toolbarButton, {
                            checked: fullComposeViewState.isRibbonShown,
                        })}
                        ariaLabel={
                            fullComposeViewState.isRibbonShown
                                ? loc(formattingOptionsTooltipHide_0)
                                : loc(formattingOptionsTooltip_0)
                        }
                        onClick={toggleRibbon}>
                        <Image
                            className={styles.image}
                            shouldFadeIn={false}
                            alt={''}
                            src={getOwaResourceUrl(SHOWRIBBON_SVG)}
                        />
                    </IconButton>
                </TooltipHost>
                {getIsDarkTheme() && (
                    <TooltipHost
                        content={
                            fullComposeViewState.isDarkMode ? loc(turnLightsOn) : loc(turnLightsOff)
                        }>
                        <IconButton
                            className={styles.toolbarButton}
                            iconProps={{
                                iconName: fullComposeViewState.isDarkMode
                                    ? ControlIcons.Sunny
                                    : ControlIcons.ClearNight,
                            }}
                            ariaLabel={
                                fullComposeViewState.isDarkMode
                                    ? loc(turnLightsOn)
                                    : loc(turnLightsOff)
                            }
                            onClick={toggleDarkMode}
                        />
                    </TooltipHost>
                )}
                {isProofingSupported() && (
                    <TooltipHost content={loc(proofingSettings_0)}>
                        <IconButton
                            className={classNames(styles.toolbarButton)}
                            onClick={showEditorSettings(fullComposeViewState.bodyViewState, window)}
                            ariaLabel={loc(proofingSettings_0)}>
                            <Image
                                className={styles.image}
                                shouldFadeIn={false}
                                alt={''}
                                src={getOwaResourceUrl(PROOFING_SVG)}
                            />
                        </IconButton>
                    </TooltipHost>
                )}
                <TooltipHost content={loc(accCheckerMenuItemText)}>
                    <IconButton
                        className={classNames(styles.toolbarButton)}
                        onClick={onOpenAccessibilityChecker}
                        ariaLabel={loc(accCheckerMenuItemText)}
                        iconProps={{ iconName: ControlIcons.AccessibiltyChecker }}
                    />
                </TooltipHost>
                <AttachmentLocalComputerFilePicker
                    ref={attachmentLocalComputerFilePicker.current}
                    fileSelectionFilter={FileSelectionFilter.None}
                    action={FilePickerAction.Attach}
                    createAttachmentHandler={aFPCreateAttachmentHandler(
                        attachmentWellHost,
                        targetWindow,
                        formId
                    )}
                />
                <AttachmentLocalComputerFilePicker
                    ref={attachmentLocalComputerToCloudFilePicker.current}
                    fileSelectionFilter={FileSelectionFilter.None}
                    action={FilePickerAction.Attach}
                    createAttachmentHandler={aFPCreateAttachmentHandlerForUploadAndShareFromCloud(
                        attachmentWellHost,
                        targetWindow,
                        formId
                    )}
                />
                <AttachmentLocalComputerFilePicker
                    ref={insertInlineImageFilePicker.current}
                    fileSelectionFilter={FileSelectionFilter.Images}
                    action={FilePickerAction.InsertInlineImage}
                    createAttachmentHandler={aFPCreateAttachmentHandler(
                        attachmentWellHost,
                        targetWindow,
                        formId
                    )}
                />
            </div>
        </div>
    );
});

async function createAttachmentsAndLogAFPDatapoint(
    files: AttachmentFileList,
    properties: CreateAttachmentsProperties,
    dataProvider: AttachmentDataProviderType,
    formId: string
) {
    createAttachmentsFromFiles(files, properties, formId);
    const logAttachOrShareDatapoint = await lazyLogAttachOrShareDatapoint.import();
    logAttachOrShareDatapoint({
        files: files,
        shouldShare: properties.shouldShare,
        isInline: properties.isInline,
        dataProviderType: dataProvider,
    });
}

function saveEventAsDraftIfNewEvent(formId: string): void {
    // Save the event as a draft if new to ensure there is
    // an event which attachments can be added to
    if (!getEventClientItemId()) {
        onIsDraftChanged(true);
        saveEventDraft(formId);
    }
}

function openCloudFilePicker(targetWindow: Window, formId: string) {
    const { event } = getComposeStore(formId);
    saveEventAsDraftIfNewEvent(formId);
    const fullComposeViewState = getFullComposeViewState(formId);
    openFilePickerAndAttach(
        false /* isInline */,
        isOnGroupCalendar(event),
        (createAttachmentHandlerParams: CreateAttachmentHandlerParams) => {
            const baseProperties = {
                isInline: createAttachmentHandlerParams.isInline,
                isHiddenInline: false,
            };
            const properties: CreateAttachmentsProperties = createAttachmentHandlerParams.shouldShare
                ? { ...baseProperties, shouldShare: true }
                : { ...baseProperties, shouldShare: false };
            createAttachmentsAndLogAFPDatapoint(
                createAttachmentHandlerParams.files,
                properties,
                createAttachmentHandlerParams.dataProviderType,
                formId
            );
        },
        fullComposeViewState.attachmentWell,
        targetWindow // only needed if calendar eventually supports projection popout
    );
}

// This function assumes the caller is an AttachmentFilePicker for logging purposes.
// Pass in an action source if calling from elsewhere.
// This function assumes the caller is an AttachmentFilePicker for logging purposes.
// Pass in an action source if calling from elsewhere.
function aFPCreateAttachmentHandler(
    attachmentWellHost: AttachmentWellHost,
    targetWindow: Window,
    formId: string
) {
    const { event } = getComposeStore(formId);
    return attachmentMultiMenuEntry.computer.getCreateAttachmentHandler(
        attachmentWellHost.attachmentWell,
        (files, properties, dataProvider) => {
            createAttachmentsAndLogAFPDatapoint(files, properties, dataProvider, formId);
        },
        isOnGroupCalendar(event) ? ComposeUniqueState.GroupCalendar : ComposeUniqueState.None,
        targetWindow
    );
}

// This function assumes the caller is an AttachmentFilePicker for logging purposes.
// Pass in an action source if calling from elsewhere.
// This function assumes the caller is an AttachmentFilePicker for logging purposes.
// Pass in an action source if calling from elsewhere.
function aFPCreateAttachmentHandlerForUploadAndShareFromCloud(
    attachmentWellHost: AttachmentWellHost,
    targetWindow: Window,
    formId: string
) {
    return attachmentMultiMenuEntry.uploadAndShareFromCloud.getCreateAttachmentHandler(
        attachmentWellHost.attachmentWell,
        (files, properties, dataProvider) => {
            createAttachmentsAndLogAFPDatapoint(files, properties, dataProvider, formId);
        },
        null /* composeUniqueState */,
        targetWindow
    );
}
